<section class="h-full flex flex-col overflow-hidden p-6">
  <!-- Header row + legend -->
  <div class="flex items-start justify-between gap-6">
    <div class="shrink-0">
      <h1 class="oswald-font text-3xl text-nside-gray-400">
        Admin Messages
      </h1>
    </div>

    <!-- Message type info popover -->
    <div class="relative group self-center">
      <button type="button" class="flex items-center gap-1.5 text-xs font-semibold hover:text-orange-600 transition-colors text-nside-orange-400">
        <lucide-icon [img]="icons.Info" [size]="16"></lucide-icon>
        Message Types
      </button>
      <div class="pointer-events-none absolute right-0 top-full mt-2 hidden group-hover:block w-72 rounded-lg border border-gray-200 bg-white shadow-lg z-30">
        <div class="flex flex-col divide-y">
          <div class="p-4 flex items-start gap-3">
            <lucide-icon [img]="icons.ShieldAlert" [size]="18" class="mt-0.5 shrink-0 text-nside-orange-400"></lucide-icon>
            <div>
              <p class="text-xs font-semibold text-gray-700">Admin Users</p>
              <p class="mt-0.5 text-[11px] text-gray-500">Sent only to Lockdown admins for the building.</p>
            </div>
          </div>
          <div class="p-4 flex items-start gap-3">
            <lucide-icon [img]="icons.MessageSquareText" [size]="18" class="mt-0.5 shrink-0 text-blue-500"></lucide-icon>
            <div>
              <p class="text-xs font-semibold text-gray-700">All Lockdown Users</p>
              <p class="mt-0.5 text-[11px] text-gray-500">Sent to every Lockdown user for the building.</p>
            </div>
          </div>
          <div class="p-4 flex items-start gap-3">
            <lucide-icon [img]="icons.FileText" [size]="18" class="mt-0.5 shrink-0 text-purple-500"></lucide-icon>
            <div>
              <p class="text-xs font-semibold text-gray-700">All App Users</p>
              <p class="mt-0.5 text-[11px] text-gray-500">Sent to all users with app access for the building.</p>
            </div>
          </div>
          <div class="p-4 flex items-start gap-3">
            <lucide-icon [img]="icons.Mail" [size]="18" class="mt-0.5 shrink-0 text-teal-500"></lucide-icon>
            <div>
              <p class="text-xs font-semibold text-gray-700">Admin Message V1</p>
              <p class="mt-0.5 text-[11px] text-gray-500">Legacy admin message.</p>
            </div>
          </div>
          <div class="p-4 flex items-start gap-3">
            <lucide-icon [img]="icons.CircleAlert" [size]="18" class="mt-0.5 shrink-0 text-gray-400"></lucide-icon>
            <div>
              <p class="text-xs font-semibold text-gray-700">Test (Self Only)</p>
              <p class="mt-0.5 text-[11px] text-gray-500">Test send — only visible to the sender.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  <div class="mt-3 flex flex-col min-h-0 rounded-lg bg-white p-6 shadow-custom-1">

    <!-- Table header row -->
    <div class="flex items-center justify-between">
      <p class="oswald-font text-lg text-nside-gray-400">Messages Sent ({{ messages.length }})</p>
    </div>

    <!-- Search + Type filter -->
    <div class="mt-3 flex items-center gap-2">
      <select
        [(ngModel)]="filterType"
        (change)="filterMessages()"
        class="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-blue-500">
        <option [ngValue]="''">All Types</option>
        <option *ngFor="let t of messageTypeOptions" [ngValue]="t.value">{{ t.label }}</option>
      </select>
      <select [class.hidden]="!userDataService.isOrg()"
        [(ngModel)]="filterClientId"
        (ngModelChange)="onClientFilterChange()"
        class="rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 focus:border-blue-500 focus:ring-blue-500">
        <option *ngFor="let c of clientOptions; trackBy: trackByClientId" [value]="c.id">{{ c.name }}</option>
      </select>
      <div class="relative flex-1">
        <div class="pointer-events-none absolute inset-y-0 start-0 flex items-center ps-3">
          <lucide-icon [img]="icons.Search" [size]="16"></lucide-icon>
        </div>
        <input
          type="search"
          [(ngModel)]="searchQuery"
          (input)="filterMessages()"
          placeholder="Search messages..."
          class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2 ps-9 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500" />
      </div>
    </div>

    <!-- Table -->
    <div class="mt-3 overflow-y-auto min-h-0">
      <ng-container *ngIf="filteredMessages.length > 0; else emptyMessages">
        <table class="w-full text-left">
          <thead class="oswald-font text-gray-700 uppercase bg-main-background sticky top-0 z-10">
            <tr>
              <th scope="col" class="px-4 py-3 w-14">No.</th>
              <th scope="col" class="px-4 py-3 min-w-40">
                <button type="button" (click)="sortBy('created')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  DATE &amp; TIME <lucide-icon [img]="icons.ArrowUpDown" [size]="14"></lucide-icon>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 min-w-40">
                <button type="button" (click)="sortBy('sender')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  SENDER <lucide-icon [img]="icons.ArrowUpDown" [size]="14"></lucide-icon>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 min-w-48">
                <button type="button" (click)="sortBy('subject')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  SUBJECT <lucide-icon [img]="icons.ArrowUpDown" [size]="14"></lucide-icon>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 min-w-32">
                <button type="button" (click)="sortBy('type')" class="flex items-center gap-1 hover:text-nside-orange-400">
                  TYPE <lucide-icon [img]="icons.ArrowUpDown" [size]="14"></lucide-icon>
                </button>
              </th>
              <th scope="col" class="px-4 py-3 min-w-32">SENT TO</th>
              <th scope="col" class="px-4 py-3 w-24">Details</th>
            </tr>
          </thead>
          <tbody>
            <tr
              *ngFor="let message of filteredMessages | paginate: { itemsPerPage: itemsPerPage, currentPage: page }; let i = index"
              class="border-b bg-white hover:bg-gray-50">
              <td class="px-4 py-3 text-gray-500">
                {{ i + 1 + (page - 1) * itemsPerPage }}
              </td>
              <td class="px-4 py-3 text-gray-600 whitespace-nowrap">{{ (message.created ? message.created + 'Z' : '') | date: 'MM/dd/yyyy - HH:mm' }}</td>
              <td class="px-4 py-3 text-gray-600 whitespace-nowrap">{{ message.senderName }}</td>
              <td class="px-4 py-3 font-medium text-gray-900 max-w-64 truncate" [title]="message.body ?? ''">
                {{ message.subject || '(No subject)' }}
              </td>
              <td class="px-4 py-3">
                <span class="rounded-full px-2.5 py-0.5 text-xs font-medium" [ngClass]="messageTypeBadgeClass(message.type)">
                  {{ messageTypeLabel(message.type) }}
                </span>
              </td>
              <td class="px-4 py-3">
                <div class="relative group/row" (mousemove)="onRowMouseMove($event)">
                  <span class="rounded-full px-2.5 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 cursor-help">
                    {{ message.sentTo.length }} recipient{{ message.sentTo.length === 1 ? '' : 's' }}
                  </span>
                  <div *ngIf="message.sentTo.length"
                    class="fixed z-50 max-w-64 px-3 py-2 text-xs text-white bg-gray-700 rounded-lg shadow-sm invisible opacity-0 group-hover/row:visible group-hover/row:opacity-100 transition-opacity pointer-events-none -translate-y-full"
                    [style.left.px]="tooltipX + 14"
                    [style.top.px]="tooltipY - 8">
                    {{ message.sentTo.join(', ') }}
                  </div>
                </div>
              </td>
              <td class="px-4 py-3">
                <button
                  type="button"
                  (click)="openDetailsModal(message)"
                  class="flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50">
                  <lucide-icon [img]="icons.Eye" [size]="12"></lucide-icon>
                  View
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </ng-container>

      <ng-template #emptyMessages>
        <div class="py-10 text-center text-sm italic text-gray-400">
          No messages sent yet.
        </div>
      </ng-template>
    </div>

    <!-- Pagination -->
    <div class="mt-3 flex items-center justify-between">
      <p class="text-sm text-gray-500">{{ filteredMessages.length }} in total</p>
      <pagination-controls
        *ngIf="filteredMessages.length > itemsPerPage"
        (pageChange)="page = $event"
        previousLabel="Prev"
        nextLabel="Next">
      </pagination-controls>
    </div>

  </div>
</section>

<app-message-details-modal
  [message]="selectedMessage"
  (closed)="closeDetailsModal()">
</app-message-details-modal>

<spinner *ngIf="loading"></spinner>
